/**
 * One voice for "the server broke": the message users see and the reference
 * they can quote to support. The reference is the value of the
 * X-FQA-Reference header every backend response carries (the request's trace
 * id when tracing is on), so support can open the exact failing request.
 */
export const SERVER_ERROR_EVENT = 'fqa:server-error';

export const REFERENCE_HEADER = 'x-fqa-reference';

export const isServerErrorStatus = (status: unknown): status is number =>
  typeof status === 'number' && status >= 500;

/**
 * Pull the reference out of a response's headers whatever shape the HTTP
 * client hands back: a plain object, an axios AxiosHeaders instance (with
 * `get`), or a fetch Headers instance.
 */
export const referenceFromHeaders = (headers: unknown): string | null => {
  if (!headers || typeof headers !== 'object') {
    return null;
  }

  const withGet = headers as { get?: (name: string) => unknown };
  if (typeof withGet.get === 'function') {
    const value = withGet.get(REFERENCE_HEADER);
    return typeof value === 'string' && value !== '' ? value : null;
  }

  const record = headers as Record<string, unknown>;
  const key = Object.keys(record).find(
    (name) => name.toLowerCase() === REFERENCE_HEADER,
  );
  const value = key ? record[key] : null;
  return typeof value === 'string' && value !== '' ? value : null;
};

export const serverErrorMessage = (reference?: string | null): string => {
  const base = "The request couldn't be completed. Try again";
  return reference
    ? `${base}, or contact support with reference ${reference}.`
    : `${base}.`;
};

/** Raise the global toast for a failed server call. */
export const notifyServerError = (reference?: string | null): void => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(SERVER_ERROR_EVENT, {
      detail: { message: serverErrorMessage(reference), reference },
    }),
  );
};

/**
 * A reference for an error that happened in the browser, where there is no
 * request to borrow a trace id from. Reported alongside the error so support
 * can search Honeycomb for it.
 */
export const clientErrorReference = (): string => {
  const bytes = new Uint8Array(6);
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    crypto.getRandomValues(bytes);
  } else {
    bytes.forEach((_, i) => {
      bytes[i] = Math.floor(Math.random() * 256);
    });
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Hand a render-time error to the global error channel so the frontend
 * telemetry records it. React 18 stops reporting errors an error boundary
 * catches, so the boundary has to do it explicitly.
 */
export const reportRenderError = (error: unknown, reference: string): void => {
  const err = error instanceof Error ? error : new Error(String(error));
  try {
    err.message = `${err.message} [ref ${reference}]`;
  } catch {
    // A frozen error still gets reported, just without the reference.
  }
  console.error(`Render error [ref ${reference}]`, err);
  if (
    typeof window !== 'undefined' &&
    typeof window.reportError === 'function'
  ) {
    window.reportError(err);
  }
};
